import { Page } from '../routes';
import { enableAdvisories, isIso, isOmniSmusAvailable, isProd } from 'src/api/config';
import { Badge, Box, Popover } from '@amzn/awsui-components-react-v3';
import React from 'react';
import { Advisory } from 'aws-sdk/clients/awsdlomni';
import { DGS_TEAM_WIKI, FEATURE_REQUEST_URL, OMNI_WIKI_PAGE } from 'src/commons/constants';
import { dataZoneIsAvailable } from 'src/components/datazone/constants';
import { getDismissedAdvisories } from 'src/components/dataadvisory/announcements/common';
import { isSearchEnabled } from 'src/commons/common';

const IMPORT_DATASETS = 'Import dataset';
export const DATA_INGESTION_TITLE = 'Data ingestion (Tethys)';

const moreInfoItems = () => {
  return [
    {
      type: 'link',
      text: 'How-to guide',
      href: OMNI_WIKI_PAGE,
      external: true,
    },
    {
      type: 'link',
      text: 'About AWS Data Governance Services',
      href: DGS_TEAM_WIKI,
      external: true,
    },
    {
      type: 'link',
      text: 'Feature request',
      href: FEATURE_REQUEST_URL,
      external: true,
    },
  ];
};

const permissionsItemsForGroup = (isIso) => {
  let items = [];
  items.push({ type: 'link', text: 'Access requests', href: Page.REQUESTS });
  if (!isIso) {
    items.push({ type: 'link', text: 'Access reviews', href: Page.REVIEWS });
  }
  items.push({ type: 'link', text: 'Datasets', href: Page.MY_DATASETS });
  items.push({ type: 'link', text: 'Groups', href: Page.GROUPS });
  return {
    type: 'section',
    text: 'Manage permissions',
    items: items,
  };
};

const permissionsItemsForWorkspace = (isConsumer) => {
  let items = [];
  items.push({
    type: 'link',
    text: 'Access requests',
    href: Page.REQUESTS,
  });
  items.push({
    type: 'link',
    text: 'Data access permissions',
    href: Page.WORKSPACE_ALL_PERMISSIONS,
  });
  items.push({ type: 'link', text: 'Workspace access', href: Page.WORKSPACEACCESS });
  if (!isConsumer) {
    items.push({ type: 'link', text: 'Permission tags', href: Page.TAGS });
    items.push({
      type: 'link',
      text: 'Fine grain access policies ',
      href: Page.FINE_GRAIN_ACCESS_POLICIES,
    });
  }
  return {
    type: 'section',
    text: 'Manage permissions',
    items: items,
  };
};

const shareItems = () => {
  return {
    type: 'section',
    text: 'Share data',
    items: [
      { type: 'link', text: IMPORT_DATASETS, href: Page.PUBLISH },
      { type: 'link', text: DATA_INGESTION_TITLE, href: Page.PUBLISHER_TABLES },
    ],
  };
};

const metricStoreItems = () => {
  return {
    type: 'section',
    text: 'MetricStore',
    items: [
      { type: 'link', text: 'Metrics catalog', href: Page.METRICS_CATALOG },
      { type: 'link', text: 'Metrics collection catalog', href: Page.METRIC_COLLECTIONS_CATALOG },
      { type: 'link', text: 'Reporting table catalog', href: Page.REPORTING_TABLE_CATALOG },
    ],
  };
};

const registerItems = () => {
  let items = [];
  items.push({ type: 'link', text: 'Lake Formation', href: Page.WORKSPACE_GLUE_REGISTER });
  if (!isIso()) {
    items.push({ type: 'link', text: 'Redshift', href: Page.WORKSPACE_REDSHIFT_REGISTER });
  }
  return {
    type: 'section',
    text: 'Register data',
    items: items,
  };
};

const dataGovernanceItems = (
  includeTemplates: boolean,
  includeGovernanceTags: boolean,
  includeMetadataEnrichment: boolean,
  includeClassification: boolean,
) => {
  let items = [];
  if (includeTemplates) {
    items.push({ type: 'link', text: 'Templates', href: Page.TEMPLATES });
  }
  if (includeGovernanceTags) {
    items.push({ type: 'link', text: 'Governance tags', href: Page.GOVERNANCE_TAGS });
    items.push({ type: 'link', text: 'Audit reports', href: Page.LIST_REPORTS });
  }
  if (includeMetadataEnrichment) {
    items.push({ type: 'link', text: 'Metadata forms', href: Page.METADATA_FORMS });
    items.push({
      type: 'link',
      text: 'Business glossaries',
      href: Page.BUSINESS_GLOSSARIES,
    });
  }
  if (includeClassification) {
    items.push({
      type: 'link',
      text: 'Classification',
      href: Page.CLASSIFICATION,
      info: (
        <Box color='text-status-info' display='inline'>
          <Popover
            header='[Preview] Classify'
            size='medium'
            triggerType='text'
            content={<>Automatic dataset classification for personal data, available for select workspaces</>}
            renderWithPortal={true}
          >
            <Box color='text-status-info' fontSize='body-s' fontWeight='bold'>
              Preview
            </Box>
          </Popover>
        </Box>
      ),
    });
  }
  return {
    type: 'section',
    text: 'Data governance',
    items: items,
  };
};

const manageResourcesItems = (includeRm, includeEventSubscriptions) => {
  let items = [];
  if (includeEventSubscriptions) {
    items.push({
      type: 'link',
      text: 'Event subscriptions',
      href: Page.EVENT_SUBSCRIPTIONS,
    });
  }
  if (includeRm) {
    items.push({ type: 'link', text: 'Resource groups', href: Page.RESOURCEGROUPS });
    items.push({ type: 'link', text: 'Bootstrap actions', href: Page.BOOTSTRAPACTIONS });
  }
  return {
    type: 'section',
    text: 'Manage resources',
    items: items,
  };
};

const dataAnalyticsItems = () => {
  return {
    type: 'section',
    text: 'Data analytics',
    items: [
      {
        type: 'link',
        text: 'Athena query',
        href: Page.WORKSPACE_RESOURCE_LINKS,
      },
      {
        type: 'link',
        text: 'Redshift query',
        href: Page.WORKSPACE_REDSHIFT_QUERY,
      },
    ],
  };
};

const workspacesItems = (isWorkspaceLevel) => {
  let items = [];
  if (isWorkspaceLevel) {
    items.push({ type: 'link', text: 'Current workspace', href: Page.WORKSPACE });
  } else {
    items.push({
      type: 'link',
      text: 'Workspaces',
      href: Page.BROWSE_WORKSPACES,
    });
  }
  return {
    type: 'section',
    text: 'Omni workspace',
    items: items,
  };
};

const datazoneItems = () => {
  let items = [];
  items.push({ type: 'link', text: 'DataZone permissions', href: Page.DATAZONE_MANAGE });
  if (dataZoneIsAvailable()) {
    return {
      type: 'section',
      text: 'DataZone resources',
      items: items,
    };
  }
  return {};
};

const toolsItems = () => {
  return {
    type: 'section',
    text: 'Tools',
    defaultExpanded: false,
    items: [{ type: 'link', text: 'Agreements playground', href: Page.TOOLS_DUA_PLAYGROUND }],
  };
};

const onboardItems = () => {
  return {
    type: 'section',
    text: 'Onboard',
    items: [{ type: 'link', text: 'Submit onboarding request', href: '/onboarding' }],
  };
};

const advisoryItem = async (props) => {
  const dismissedAdvisories = await getDismissedAdvisories();
  const advisoriesToDisplay = props.activeAdvisories.filter(
    (advisory: Advisory) => !dismissedAdvisories.includes(advisory.advisoryId),
  );
  return {
    type: 'link',
    text: 'Data advisories',
    href: Page.LIST_ADVISORIES,
    info: advisoriesToDisplay.length > 0 ? <Badge color='red'>{advisoriesToDisplay.length}</Badge> : undefined,
  };
};

const smusItems = () => {
  let items = [];
  items.push({ type: 'link', text: `${isProd() ? 'Domain' : 'Sandbox domain'} resources`, href: Page.SMUS_RESOURCES });
  if (isOmniSmusAvailable()) {
    return {
      type: 'section',
      text: `AWS-SMUS ${isProd() ? '' : 'sandbox'}`,
      items: items,
    };
  }
  return {};
};

// the "Discover data" section of the sidebar only displays "Data advisories" in certain regions, and
// it displays a red badge next to "Data advisories" if the user has active advisories.
let browseItemsForGroup = async (props) => {
  let items = [];
  if (isSearchEnabled()) {
    items.push({ type: 'link', text: 'Search', href: Page.SEARCH });
  }
  items.push(
    { type: 'link', text: 'Browse datasets', href: Page.BROWSE_DATASETS },
    { type: 'link', text: 'Browse databases', href: Page.BROWSE_DATABASES },
    { type: 'link', text: 'Browse catalogs', href: Page.BROWSE_CATALOGS },
  );

  if (enableAdvisories() && props.userInfo) {
    items.push(await advisoryItem(props));
  }
  return {
    type: 'section',
    text: 'Discover data',
    items: items,
  };
};
let browseItemsForWorkspace = async (props) => {
  let items = [];
  if (isSearchEnabled()) {
    items.push({ type: 'link', text: 'Search', href: Page.SEARCH });
  }
  items.push(
    { type: 'link', text: 'Browse datasets', href: Page.WORKSPACES_BROWSE_DATASETS },
    { type: 'link', text: 'Browse databases', href: Page.WORKSPACES_BROWSE_DATABASES },
    { type: 'link', text: 'Browse catalogs', href: Page.WORKSPACES_BROWSE_CATALOGS },
  );

  if (enableAdvisories()) {
    items.push(await advisoryItem(props));
  }
  return {
    type: 'section',
    text: 'Discover data',
    items: items,
  };
};

const divider = () => {
  return { type: 'divider' };
};

// Navigation for new customers
export const getNavItemsNewCustomers = async (props) => [
  await browseItemsForGroup(props),
  onboardItems(),
  smusItems(),
  divider(),
  ...moreInfoItems(),
];

// Navigation for groups
export const getNavItemsForGroupDataLakeAdmins = async (props) => {
  let navItemsForAdmins = [];
  navItemsForAdmins.push(await browseItemsForGroup(props));
  navItemsForAdmins.push(shareItems());
  if (isIso()) {
    navItemsForAdmins.push(permissionsItemsForGroup(true));
    navItemsForAdmins.push(dataGovernanceItems(true, false, true, false));
    navItemsForAdmins.push(manageResourcesItems(true, false));
  } else {
    navItemsForAdmins.push(metricStoreItems());
    navItemsForAdmins.push(permissionsItemsForGroup(false));
    navItemsForAdmins.push(dataGovernanceItems(true, true, true, true));
    navItemsForAdmins.push(manageResourcesItems(true, true));
  }
  navItemsForAdmins.push(workspacesItems(false));
  navItemsForAdmins.push(datazoneItems());
  navItemsForAdmins.push(smusItems());
  navItemsForAdmins.push(toolsItems());
  navItemsForAdmins.push(divider());
  navItemsForAdmins.push(...moreInfoItems());
  return navItemsForAdmins;
};

export const getNavItemsForGroupProvider = async (props) => {
  let navItemsForGroupProvider = [];
  navItemsForGroupProvider.push(await browseItemsForGroup(props));
  navItemsForGroupProvider.push(shareItems());
  if (isIso()) {
    navItemsForGroupProvider.push(permissionsItemsForGroup(true));
    navItemsForGroupProvider.push(dataGovernanceItems(true, false, true, false));
  } else {
    navItemsForGroupProvider.push(metricStoreItems());
    navItemsForGroupProvider.push(permissionsItemsForGroup(false));
    navItemsForGroupProvider.push(dataGovernanceItems(true, true, true, true));
  }
  navItemsForGroupProvider.push(manageResourcesItems(true, false));
  navItemsForGroupProvider.push(workspacesItems(false));
  navItemsForGroupProvider.push(datazoneItems());
  navItemsForGroupProvider.push(smusItems());
  navItemsForGroupProvider.push(divider());
  navItemsForGroupProvider.push(...moreInfoItems());
  return navItemsForGroupProvider;
};

export const getNavItemsForGroupConsumer = async (props) => {
  let navItemsForGroupConsumer = [];
  navItemsForGroupConsumer.push(await browseItemsForGroup(props));
  if (isIso()) {
    navItemsForGroupConsumer.push(permissionsItemsForGroup(true));
  } else {
    navItemsForGroupConsumer.push(metricStoreItems());
    navItemsForGroupConsumer.push(permissionsItemsForGroup(false));
    navItemsForGroupConsumer.push(manageResourcesItems(true, false));
  }
  navItemsForGroupConsumer.push(workspacesItems(false));
  navItemsForGroupConsumer.push(datazoneItems());
  navItemsForGroupConsumer.push(smusItems());
  navItemsForGroupConsumer.push(divider());
  navItemsForGroupConsumer.push(...moreInfoItems());
  return navItemsForGroupConsumer;
};

// Navigation for workspaces
export const getNavItemsForWorkspacesProvider = async (props) => {
  let navItemsForWorkspaceProvider = [];
  navItemsForWorkspaceProvider.push(await browseItemsForWorkspace(props));
  navItemsForWorkspaceProvider.push(registerItems());
  navItemsForWorkspaceProvider.push(permissionsItemsForWorkspace(false));
  if (isIso()) {
    navItemsForWorkspaceProvider.push(dataGovernanceItems(true, false, true, false));
  } else {
    navItemsForWorkspaceProvider.push(dataGovernanceItems(true, true, true, true));
  }
  navItemsForWorkspaceProvider.push(manageResourcesItems(false, true));
  navItemsForWorkspaceProvider.push(dataAnalyticsItems());
  navItemsForWorkspaceProvider.push(workspacesItems(true));
  navItemsForWorkspaceProvider.push(datazoneItems());
  navItemsForWorkspaceProvider.push(smusItems());
  navItemsForWorkspaceProvider.push(divider());
  navItemsForWorkspaceProvider.push(...moreInfoItems());
  return navItemsForWorkspaceProvider;
};

export const getNavItemsForWorkspacesConsumer = async (props) => {
  let navItemsForWorkspaceConsumer = [];
  navItemsForWorkspaceConsumer.push(await browseItemsForWorkspace(props));
  navItemsForWorkspaceConsumer.push(permissionsItemsForWorkspace(true));
  navItemsForWorkspaceConsumer.push(dataAnalyticsItems());
  navItemsForWorkspaceConsumer.push(workspacesItems(true));
  navItemsForWorkspaceConsumer.push(datazoneItems());
  navItemsForWorkspaceConsumer.push(smusItems());
  navItemsForWorkspaceConsumer.push(divider());
  navItemsForWorkspaceConsumer.push(...moreInfoItems());
  return navItemsForWorkspaceConsumer;
};

export const appLayoutLabels = {
  navigation: 'Side navigation',
  navigationToggle: 'Open side navigation',
  navigationClose: 'Close side navigation',
  tools: 'Help panel',
  toolsToggle: 'Open help panel',
  toolsClose: 'Close help panel',
};

export const activeHref = '/subscription';
